import { Input as TamaguiInput, styled } from 'tamagui';

import { MICRO_TRANSITION_BORDER } from '@/theme/motion';
import { borderWidth } from '@/theme/tokens.generated';

// Wraps Tamagui's own Input rather than touching every call site or
// tamagui.config.ts's global `defaultProps` (that config-level route
// broke shorthand prop inference -- `px`, `pb`, etc. -- across every
// unrelated screen in the app; see tamagui.config.ts's own note on
// this). Two fixes, both scoped here once:
//
// 1. borderRadius: '$xl' -- matches Card/PrimaryButton's
//    shared rounded base (tokens/borders.json's $xl, 18px). Input/
//    TextArea used to sit on the brand's 2px "stamped" radius while
//    every other surface moved to $xl on 2026-09-16 -- founder flagged
//    the resulting inconsistency directly ("all text boxes should be
//    rounded more and have a common base"), so both now match instead
//    of carrying a separate signature. Border COLOR is fixed separately, at the theme level
//    (tamagui.config.ts's light_Input/dark_Input sub-themes), since
//    that part DOES cascade cleanly through Tamagui's theme system
//    without this wrapper.
// 2. transition: MICRO_TRANSITION_BORDER -- Tamagui's web CSS driver
//    defaults an unset `transition` to `all <duration>` whenever a
//    component has hoverStyle/focusStyle (verified live via
//    getComputedStyle -- see src/theme/motion.ts), which declares
//    padding (and everything else) as transitioned even though
//    Input's own hover/focus styles only ever change borderColor.
// `name: 'Input'` (not a new name) is deliberate -- keeps this wrapper
// resolving the SAME tamagui.config.ts sub-themes (light_Input/
// dark_Input) that fix the border color, rather than needing a second,
// duplicate pair of sub-themes for a differently-named component.
//
// hoverStyle/focusStyle are re-declared here (not left inherited from
// @tamagui/input's own shared.mjs defaults) because `transition` lives
// INSIDE a pseudo-style object, not as a top-level prop -- a top-level
// `transition` crashes at runtime with a raw CSS string (see
// primary-button.tsx's doc comment) -- and re-declaring them here is
// also how the fix stays guaranteed-correct regardless of whether
// styled() merges or replaces an inherited pseudo-style object.
// borderColor values match @tamagui/input's own shared.mjs exactly
// (`$borderColorHover` / `$borderColorFocus`) -- only `transition` is
// new.
export const Input = styled(TamaguiInput, {
  name: 'Input',
  borderRadius: '$md',
  height: 48,
  paddingHorizontal: '$4',
  fontSize: '$body',
  borderWidth: borderWidth.hairline,
  hoverStyle: {
    borderColor: '$borderColorHover',
    transition: MICRO_TRANSITION_BORDER,
  },
  focusStyle: {
    borderColor: '$borderColorFocus',
    // 2px focus ring drawn inward over the 1px border: the box never changes
    // width, so nothing shifts when a field gains focus.
    outlineWidth: borderWidth.control,
    outlineStyle: 'solid',
    outlineColor: '$borderColorFocus',
    outlineOffset: -borderWidth.control,
    transition: MICRO_TRANSITION_BORDER,
  },
});
